import React, { FC, useContext, useMemo } from "react";
import { PaymentContext } from "v3/context/payment";
import { OrderMoreThanAllocationAlert } from "v3/components/checkout/alerts/OrderMoreThanAllocationAlert";
import { SavedOption } from "v3/components/checkout/SavedOption";
import { AllocationOption } from "v3/components/payment/Allocation/AllocationOption";
import { PayPalOption } from "v3/components/payment/paypal/PayPalOption";
import { InvoiceOption } from "v3/components/payment/Invoice/InvoiceOption";
import { getIsOrderMoreThanAllocationAmount } from "v3/helpers/checkout";
import { getCreditCardImg, getDisplayCreditCardName } from "v3/helpers/payment";
import { AllowedPaymentMethod } from "v3/services/graphql/generated";
import { SavedPaymentMethodsType } from "v3/types/payment";
import { formatDollarsFromCents } from "helpers/currency";
import { StyledSavedPaymentMethods } from "./index.styled";

interface SavedPaymentMethodsProps {
  closeNewPaymentForm: () => void;
  paymentMethods: SavedPaymentMethodsType;
  selectedToken?: string | null;
  onlyAllowAllocation?: boolean;
  totalPrice?: number;
}

export const SavedPaymentMethods: FC<SavedPaymentMethodsProps> = ({
  closeNewPaymentForm,
  paymentMethods,
  selectedToken,
  onlyAllowAllocation,
  totalPrice,
}) => {
  const {
    handleSelectSavedPayPal,
    handleSelectSavedCreditCard,
    handleSelectSavedAllocatedCredit,
    paymentType,
  } = useContext(PaymentContext);

  const handlePayPalSelect = (token: string) => {
    closeNewPaymentForm();
    handleSelectSavedPayPal({ token });
  };

  const isOrderMoreThanAllocationAmount = useMemo(
    () =>
      getIsOrderMoreThanAllocationAmount({
        paymentMethods,
        totalPrice,
      }),
    [totalPrice, paymentMethods]
  );

  return (
    <>
      {isOrderMoreThanAllocationAmount && <OrderMoreThanAllocationAlert />}
      <StyledSavedPaymentMethods>
        {paymentMethods.map((paymentMethod) => {
          if (paymentMethod.__typename === "CreditCard") {
            const {
              billingAddress,
              cardType,
              cardholderName,
              isDefault,
              lastFour,
              token,
            } = paymentMethod;
            const title = `${getDisplayCreditCardName(
              cardType
            )} ending in ${lastFour}`;
            return (
              <SavedOption
                checked={token === selectedToken}
                handleClick={() =>
                  handleSelectSavedCreditCard({
                    billingAddress,
                    token,
                  })
                }
                imageAlt={cardType}
                imageSrc={getCreditCardImg(cardType)}
                isDefault={isDefault}
                key={token}
                title={title}
                isDisabled={onlyAllowAllocation}
              >
                <div>{cardholderName}</div>
                <div>{billingAddress?.line1}</div>
              </SavedOption>
            );
          }

          if (paymentMethod.__typename === "PaypalAccount") {
            const { email, isDefault, token } = paymentMethod;
            return (
              <PayPalOption
                checked={token === selectedToken}
                email={email}
                isDefault={isDefault}
                onSelect={handlePayPalSelect}
                token={token}
                key={token}
                isDisabled={onlyAllowAllocation}
              />
            );
          }

          if (paymentMethod.__typename === "Invoice") {
            return (
              <InvoiceOption
                checked
                // currently only invoice can be selected if enabled for account
                onSelect={() => {}}
                key="invoice"
                isDisabled={onlyAllowAllocation}
              />
            );
          }

          if (paymentMethod.__typename === "Credit") {
            const { availableAmount, id } = paymentMethod;
            return (
              <AllocationOption
                balance={formatDollarsFromCents(availableAmount)}
                isDefault={false}
                checked={paymentType === AllowedPaymentMethod.AllocatedCredit}
                onSelect={() => handleSelectSavedAllocatedCredit({ id })}
                key={id}
                isDisabled={isOrderMoreThanAllocationAmount}
              />
            );
          }
        })}
      </StyledSavedPaymentMethods>
    </>
  );
};
